// AddStoreSellerPaymentModal.js
import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Alert, Spinner } from "react-bootstrap";
import axios from "axios";

export default function AddStoreSellerPaymentModal({
    show,
    onHide,
    storeSellerId,
    sellerInfo,
    summary,
    t,
    onSuccess,
}) {
    const isPayable = sellerInfo?.settlement_type === "payable";
    const actionLabel = isPayable ? t("Pay") : t("Receive");
    const remainingLabel = isPayable ? t("Remaining to Pay") : t("Remaining to Receive");

    const [formData, setFormData] = useState({
        bank_id: "",
        amount: "",
        payment_date: new Date().toISOString().split("T")[0],
        notes: "",
    });

    const [banks, setBanks] = useState([]);
    const [loadingBanks, setLoadingBanks] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    // Fetch Banks list on modal show
    useEffect(() => {
        if (show) {
            fetchBanks();
        }
    }, [show]);

    const fetchBanks = async () => {
        setLoadingBanks(true);
        setError(null);
        try {
            const token = localStorage.getItem("authToken");
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/admin/banks`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            if (response.data?.success) {
                setBanks(response.data.data || response.data.banks || []);
            } else if (Array.isArray(response.data)) {
                setBanks(response.data);
            }
        } catch (err) {
            console.error("Error fetching banks:", err);
            setError(err.response?.data?.message || "Failed to load banks list.");
        } finally {
            setLoadingBanks(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);

        try {
            const token = localStorage.getItem("authToken");
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/admin/store-sellers/${storeSellerId}/add-payment`,
                formData,
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            if (response.data?.success) {
                onSuccess(response.data.message || "Payment processed successfully!");
                onHide();
                setFormData({
                    bank_id: "",
                    amount: "",
                    payment_date: new Date().toISOString().split("T")[0],
                    notes: "",
                });
            }
        } catch (err) {
            console.error("Error adding payment:", err);
            setError(err.response?.data?.message || "Failed to submit settlement payment.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title className="fs-5 fw-bold">{actionLabel} {t("Settlement Amount")}</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body className="p-4">
                    {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

                    {/* Summary Info */}
                    <div className="bg-light p-3 rounded-3 mb-3 border">
                        <div className="d-flex justify-content-between mb-1">
                            <span className="text-muted small">{t("Settlement Type")}:</span>
                            <span className="fw-semibold text-uppercase">{sellerInfo?.settlement_type || "N/A"}</span>
                        </div>
                        <div className="d-flex justify-content-between">
                            <span className="text-muted small">{remainingLabel}:</span>
                            <span className="fw-bold text-danger">${Number(summary?.remaining_amount || 0).toFixed(2)}</span>
                        </div>
                    </div>

                    {/* Bank Selection */}
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold">{t("Bank")} *</Form.Label>
                        {loadingBanks ? (
                            <div className="d-flex align-items-center gap-2 text-muted small py-2">
                                <Spinner animation="border" size="sm" />
                                {t("Loading banks...")}
                            </div>
                        ) : (
                            <Form.Select
                                name="bank_id"
                                value={formData.bank_id}
                                onChange={handleChange}
                                required
                            >
                                <option value="">{t("Select Bank")}</option>
                                {banks.map((bank) => (
                                    <option key={bank.id} value={bank.id}>
                                        {bank.name || bank.title} {bank.account_number ? `(${bank.account_number})` : ""}
                                    </option>
                                ))}
                            </Form.Select>
                        )}
                    </Form.Group>

                    {/* Amount */}
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold">{actionLabel} {t("Amount")} *</Form.Label>
                        <Form.Control
                            type="number"
                            step="0.01"
                            name="amount"
                            placeholder="0.00"
                            value={formData.amount}
                            onChange={handleChange}
                            max={summary?.remaining_amount}
                            required
                        />
                    </Form.Group>

                    {/* Payment Date */}
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold">{t("Payment Date")} *</Form.Label>
                        <Form.Control
                            type="date"
                            name="payment_date"
                            value={formData.payment_date}
                            onChange={handleChange}
                            required
                        />
                    </Form.Group>

                    {/* Notes */}
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold">{t("Notes")}</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            name="notes"
                            placeholder={t("Optional transaction notes...")}
                            value={formData.notes}
                            onChange={handleChange}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={submitting}>
                        {t("Cancel")}
                    </Button>
                    <Button variant="primary" type="submit" disabled={submitting || loadingBanks}>
                        {submitting ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                {t("Processing...")}
                            </>
                        ) : (
                            <>{actionLabel} {t("Payment")}</>
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}